import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { LangProvider } from "@/lib/lang";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Secteurs from "@/pages/Secteurs";
import APropos from "@/pages/APropos";
import Contact from "@/pages/Contact";
import Ressources from "@/pages/Ressources";
import Article from "@/pages/Article";
import Solutions from "@/pages/Solutions";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Moon from "@/pages/Moon";
import MoonSearch from "@/pages/MoonSearch";
import Fournisseurs from "@/pages/Fournisseurs";
import Catalogue from "@/pages/Catalogue";
import ClientAuth from "@/pages/ClientAuth";
import ClientDashboard from "@/pages/ClientDashboard";
import NotFound from "@/pages/NotFound";
import ConsentBanner from "@/components/ConsentBanner";
import AssistantWidget from "@/components/AssistantWidget";
import CrmLayout from "@/pages/crm/CrmLayout";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import CrmActivities from "@/pages/crm/CrmActivities";
import DataModule from "@/pages/crm/DataModule";
import { initAnalytics } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let lenis;
    let rafId;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      const raf = (time) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
    };
  }, []);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin") || pathname.startsWith("/crm");
  return (
    <>
      {!isAdmin && <Nav />}
      {!isAdmin && <AssistantWidget />}
      {!isAdmin && <ConsentBanner />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/moon" element={<Moon />} />
        <Route path="/moon/recherche" element={<MoonSearch />} />
        <Route path="/catalogue" element={<Catalogue />} />
        <Route path="/client" element={<ClientAuth />} />
        <Route path="/client/tableau-de-bord" element={<ClientDashboard />} />
        <Route path="/fournisseurs" element={<Fournisseurs />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/secteurs" element={<Secteurs />} />
        <Route path="/a-propos" element={<APropos />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/ressources" element={<Ressources />} />
        <Route path="/ressources/:slug" element={<Article />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/confidentialite" element={<Legal kind="privacy" />} />
        <Route path="/mentions-legales" element={<Legal kind="mentions" />} />
        <Route path="/conditions" element={<Legal kind="conditions" />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/tableau-de-bord" element={<AdminDashboard />} />
        <Route path="/crm" element={<CrmLayout />}>
          <Route index element={<CrmDashboard />} />
          <Route path="tableau-de-bord" element={<CrmDashboard />} />
          <Route path="entreprises" element={<DataModule moduleKey="companies" />} />
          <Route path="contacts" element={<DataModule moduleKey="contacts" />} />
          <Route path="leads" element={<DataModule moduleKey="leads" />} />
          <Route path="opportunites" element={<DataModule moduleKey="opportunities" />} />
          <Route path="activites" element={<CrmActivities />} />
          <Route path="taches" element={<DataModule moduleKey="tasks" />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <Footer />}
    </>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-navy-950 px-5 text-center">
          <div>
            <h1 className="font-display text-2xl text-white">Une erreur est survenue.</h1>
            <a href="/" className="mt-6 inline-block rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white">Recharger</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <ErrorBoundary>
      <LangProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SmoothScroll />
          <Shell />
        </BrowserRouter>
      </LangProvider>
    </ErrorBoundary>
  );
}

export default App;
